import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { PlugAvatar } from '../components/PlugAvatar';
import { useReplyThread } from '../hooks/useReplyThread';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { useInboxAttachments } from '../hooks/useInboxAttachments';
import type { ReplyThread } from '../outreachTypes';
import { shortDate } from '../outreachFormat';
import { inboxMessages as messages } from './inboxMessages';
import { answerBlockMessage } from './answerBlockMessage';
import { insertAttachmentLine } from './insertAttachmentLine';
import { AttachmentChips } from './AttachmentChips';
import { PressPhotoMissing } from './PressPhotoMissing';
import { LiveLinkReminderChip } from './LiveLinkReminderChip';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Inbox.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The answer (design outreach-v2 m1 "Plug's answer", m2 "A short thank-you"): the suggestion fills
 * the field; the attachment chips add link lines; the person edits and sends it from the same
 * mailbox, in the thread. Autopilot never sends a reply: only this click does.
 */
const ReplyAnswerComponent: React.FC<{ accountId: number; thread: ReplyThread }> = ({ accountId, thread }) => {
  const intl = useIntl();
  const { isDemo } = useOutreachPlan();
  const { sendAnswer, isSending, sendError, markHandled } = useReplyThread(accountId, thread.replyUuid);
  const blocked = answerBlockMessage(thread);
  const open = thread.answeredAt === null && blocked === null;
  const attachments = useInboxAttachments(accountId, thread.replyUuid, open);
  const thankYou = thread.group === 'added' && thread.suggestedAnswer === null;
  const fallback = thankYou ? intl.formatMessage(messages.thanks, { song: thread.songTitle }) : '';
  const [body, setBody] = useState(thread.suggestedAnswer ?? fallback);
  const cursor = useRef<number | null>(null);
  useEffect(() => {
    setBody(thread.suggestedAnswer ?? fallback);
    cursor.current = null;
  }, [fallback, thread.replyUuid, thread.suggestedAnswer]);
  const addLine = useCallback((line: string) => {
    setBody((current) => {
      const next = insertAttachmentLine(current, line, cursor.current);
      cursor.current = next.cursor;
      return next.text;
    });
  }, []);
  const keepCursor = (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
    cursor.current = event.currentTarget.selectionEnd;
  };

  if (thread.answeredAt !== null) {
    return (
      <p className={shared.small}>{intl.formatMessage(messages.sent, { date: shortDate(intl, thread.answeredAt) })}</p>
    );
  }
  const markHandledButton = thread.handledAt === null && (
    <SMLButton
      size="sm"
      outline
      omitCap
      onClick={() => markHandled(thread.replyUuid).catch(console.error)}
      disabled={isDemo}
    >
      {intl.formatMessage(thankYou ? messages.skip : messages.markHandled)}
    </SMLButton>
  );
  if (blocked !== null) {
    // `inbox.answer` would refuse it: no field, no Send, and the reason instead.
    return (
      <div className={styles.answer} data-testid="reply-answer-blocked">
        <p className="text-warning small mb-0" role="status">
          {withBrandWord(intl.formatMessage(blocked.descriptor, blocked.values))}
        </p>
        <div className={shared.row}>
          <span className={shared.grow} />
          {markHandledButton}
        </div>
      </div>
    );
  }
  let title = messages.answerLabel;
  if (thread.suggestedAnswer !== null) {
    title = messages.suggested;
  } else if (thankYou) {
    title = messages.thankYouTitle;
  }
  return (
    <div className={styles.answer} data-testid="reply-answer">
      <span className={styles.answerHead}>
        {thread.suggestedAnswer !== null && <PlugAvatar size={16} />}
        {intl.formatMessage(title)}
        <span className={shared.grow} />
        <span className={styles.answerFrom}>
          {intl.formatMessage(messages.from, { address: thread.message.fromAddress })}
        </span>
      </span>
      <textarea
        className={shared.textarea}
        rows={6}
        value={body}
        maxLength={5000}
        aria-label={intl.formatMessage(messages.answerLabel)}
        onChange={(event) => {
          setBody(event.target.value);
          keepCursor(event);
        }}
        onSelect={keepCursor}
      />
      <AttachmentChips attachments={attachments} body={body} disabled={isDemo} onAdd={addLine} />
      <PressPhotoMissing thread={thread} attachments={attachments} />
      {sendError !== null && <p className="text-danger small mb-0">{intl.formatMessage(messages.sendFailed)}</p>}
      <div className={shared.row}>
        <LiveLinkReminderChip accountId={accountId} thread={thread} attachments={attachments} disabled={isDemo} />
        <span className={shared.grow} />
        {markHandledButton}
        <SMLButton
          color="primary"
          size="sm"
          omitCap
          leftIcon={faPaperPlane}
          isLoading={isSending}
          disabled={isDemo || !thread.canAnswer || body.trim() === ''}
          onClick={() => sendAnswer({ replyUuid: thread.replyUuid, body }).catch(console.error)}
        >
          {intl.formatMessage(messages.send)}
        </SMLButton>
      </div>
    </div>
  );
};

export const ReplyAnswer = TypedMemo(ReplyAnswerComponent);
